import React, { useCallback, useMemo } from 'react'
import { Box, Chip } from '@mui/material'
import { advancedColors, surfaces } from '../theme'
import { TourStepDefinition } from '../tour/steps'
import TourCard, { TourCardProps } from '../tour/TourCard'
import TourRunner from '../tour/TourRunner'
import { ProductUpdate, ProductUpdateKind, ProductUpdateMedia } from './updates'
import { useProductUpdatesWalkthrough } from './ProductUpdatesProvider'

const KIND_TAGS: Record<ProductUpdateKind, { label: string; color: string }> = {
  new: { label: 'NEW', color: 'primary.main' },
  fix: { label: 'FIX', color: advancedColors.amber },
  improved: { label: 'IMPROVED', color: surfaces.blue.fg },
}

const EMPTY_STEP: TourStepDefinition = {
  id: 'product-updates-empty',
  title: 'No product updates yet',
  body: 'Changes to the dashboard will be announced here.',
}

const INTRO_STEP: TourStepDefinition = {
  id: 'product-updates-intro',
  title: 'Product updates',
  body: 'There have been changes since you last used the dashboard. Click through them now and pay attention to them: they might change what you can/cannot do.',
}

const noTabs = () => undefined

const toStep = ({ id, title, body, target }: ProductUpdate): TourStepDefinition => ({
  id,
  title,
  body,
  selector: target?.selector,
  placement: target?.placement,
  opensCareTeamChat: target?.opensCareTeamChat,
})

const UpdateTag = ({ kind }: { kind: ProductUpdateKind }) => (
  <Chip
    label={KIND_TAGS[kind].label}
    size="small"
    sx={{
      flexShrink: 0,
      height: 20,
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      bgcolor: KIND_TAGS[kind].color,
      color: 'primary.contrastText',
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)

const UpdateMedia = ({ media }: { media: ProductUpdateMedia }) => (
  <Box
    sx={{
      mt: 1.5,
      aspectRatio: '16 / 9',
      p: 1,
      borderRadius: 1,
      border: 1,
      borderColor: 'divider',
      backgroundColor: advancedColors.greenSoft,
      '& > img, & > video': { display: 'block', width: '100%', height: '100%', objectFit: 'contain' },
    }}
  >
    {media.kind === 'image' ? (
      <img src={media.src} alt={media.alt} />
    ) : (
      <video src={media.src} aria-label={media.alt} controls />
    )}
  </Box>
)

const ProductUpdatesTour = () => {
  const { view, close, markSeen, setCareTeamChatOpen } = useProductUpdatesWalkthrough()
  const updates = view?.updates
  const mode = view?.mode
  const steps = useMemo(() => {
    if (!updates || updates.length === 0) return [EMPTY_STEP]
    return mode === 'unseen' ? [INTRO_STEP, ...updates.map(toStep)] : updates.map(toStep)
  }, [updates, mode])
  const byId = useMemo(() => new Map((updates ?? []).map((update) => [update.id, update])), [updates])

  const onStepShown = useCallback(
    (step: TourStepDefinition) => {
      if (byId.has(step.id)) markSeen([step.id]).catch(() => undefined)
    },
    [byId, markSeen]
  )

  const renderCard = useCallback(
    (props: TourCardProps) => {
      const update = byId.get(props.step.id)

      return (
        <TourCard
          {...props}
          tag={update ? <UpdateTag kind={update.kind} /> : props.step === INTRO_STEP && <UpdateTag kind="new" />}
          skipLabel={null}
          closeLabel="Close product updates"
        >
          {update?.media && <UpdateMedia media={update.media} />}
        </TourCard>
      )
    },
    [byId]
  )

  if (!view) {
    return null
  }

  return (
    <TourRunner
      steps={steps}
      openTab={noTabs}
      onEnd={close}
      setCareTeamChatOpen={setCareTeamChatOpen}
      missingTarget="centre"
      onStepShown={onStepShown}
      renderCard={renderCard}
    />
  )
}

export default ProductUpdatesTour
